import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { useFavorites } from '@/hooks/useFavorites';
import { getMovieById } from '@/lib/queries';
import { useSeo } from '@/hooks/useSeo';

export default function FavoritesPage() {
  const { ids, count, toggle } = useFavorites();
  const navigate = useNavigate();
  const list = ids.map((id) => getMovieById(id)).filter((movie) => Boolean(movie));

  useSeo({
    title: '我的收藏 - 日韩成人电影',
    description: '查看你在本站收藏的日韩影片。收藏记录保存在当前浏览器本地，无需登录，清除浏览器数据后会一并清空。',
    keywords: '我的收藏,日韩成人电影收藏,本地收藏,影片收藏夹',
  });

  return (
    <>
      <PageHeader
        eyebrow="My collection"
        title="我的收藏"
        description="收藏记录只保存在当前浏览器本地，不会上传到服务器，也不需要注册登录。换一台设备或清除浏览器数据后，收藏会重新开始。"
        crumbs={[{ label: '我的收藏' }]}
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <i className="ri-heart-3-line text-sm text-primary-300" />
              已收藏 {count} 部影片
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-database-2-line text-sm text-primary-300" />
              本地存储，无需登录
            </span>
          </>
        }
      />

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        {list.length === 0 ? (
          <EmptyState
            icon="ri-heart-line"
            title="收藏夹还是空的"
            description="在任意影片详情页点击「收藏这部影片」，它就会出现在这里。先去首页看看本周的焦点推荐吧。"
            primaryLabel="去首页看看"
            primaryTo="/"
            resetLabel="浏览分类"
            onReset={() => navigate('/category/drama')}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-800 pb-4">
              <p className="text-xs text-foreground-500">
                共 {list.length} 部影片，按收藏时间排序
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/search"
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-700 px-3 py-1.5 text-xs text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  搜索更多影片
                </Link>
                <button
                  type="button"
                  onClick={() => ids.forEach((id) => toggle(id))}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-700 px-3 py-1.5 text-xs text-foreground-500 transition-colors hover:border-primary-500 hover:text-primary-300"
                >
                  清空收藏
                </button>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4 lg:gap-x-6">
              {list.map((movie) =>
                movie ? (
                  <div key={movie.id}>
                    <MovieCard movie={movie} />
                    <button
                      type="button"
                      onClick={() => toggle(movie.id)}
                      className="mt-2 inline-flex cursor-pointer items-center gap-1 text-[11px] text-foreground-500 transition-colors hover:text-primary-300"
                    >
                      <i className="ri-heart-fill text-xs text-primary-400" />
                      取消收藏
                    </button>
                  </div>
                ) : null,
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}